/**
 * Font previews for the font picker (Opmaak › Global › Font): every option in
 * the dropdown is shown in its own typeface, so you see it before you pick it.
 *
 * Only what is on screen is loaded. The listbox watches its options with an
 * IntersectionObserver; the families that scroll into view are fetched from
 * Google Fonts in batches of up to 20, and with `text=` so each font only
 * carries the letters of its own name (a few hundred bytes instead of a full
 * font). Scrolling quickly shows the regular font for a moment, then swaps.
 *
 * The preview faces get their OWN family name ("<Family> sftpv"). A `text=`
 * subset registered under the real name would compete with the full font the
 * mockup loads for the selected family, and letters outside the subset would
 * then fall back to another font in the mockup.
 *
 * Skipped: system stacks ("Arial, Helvetica, sans-serif", already local) and
 * icon/symbol fonts (their name would render as pictograms).
 */

const SUFFIX = ' sftpv';
const BATCH = 20;
const requested = new Set();

const isSystemStack = (family) => String(family).includes(',');
export const isSymbolFont = (family) => /\b(icons?|symbols?|emoji)\b/i.test(String(family));

const GENERIC = { serif: 'serif', 'sans-serif': 'sans-serif', monospace: 'monospace', handwriting: 'cursive', display: 'sans-serif' };

/** The CSS font-family to show an option in (undefined = the regular UI font). */
export const fontPreviewStack = (opt) => {
    const family = opt?.family;
    if (!family) return undefined;
    if (isSystemStack(family)) return family;
    if (isSymbolFont(family)) return undefined;
    const generic = GENERIC[opt.category] || 'sans-serif';
    // the preview face first; the real name next (fully loaded for the
    // selected font); the generic family while nothing has arrived yet
    return `"${family}${SUFFIX}", "${family}", ${generic}`;
};

const cssUrl = (families) => {
    const params = families.map((f) => 'family=' + encodeURIComponent(f).replace(/%20/g, '+')).join('&');
    const text = Array.from(new Set(families.join('').split(''))).join('');
    return `https://fonts.googleapis.com/css2?${params}&text=${encodeURIComponent(text)}&display=swap`;
};

// Fallback when the browser will not let us `fetch` the CSS (an ad/privacy
// blocker, a proxy stripping CORS): a plain <link> still gets through, like
// the one that loads the selected font. The faces then keep their real name,
// which is why the currently selected family is never previewed (see
// fontPreviewListboxRef) -- its full font is already loaded and a later
// name-only subset would win over it in the mockup.
let linkMode = false;
const linkBatch = (families) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl(families);
    link.setAttribute('data-sft-font-preview', families.length);
    document.head.appendChild(link);
};

const fetchBatch = async (families) => {
    if (linkMode) { linkBatch(families); return; }
    let res;
    try {
        res = await fetch(cssUrl(families));
    } catch {
        // the request itself was refused (not an HTTP error): switch over
        linkMode = true;
        linkBatch(families);
        return;
    }
    try {
        if (!res.ok) throw new Error(String(res.status));
        const css = await res.text();
        // rename every face to its preview alias
        const renamed = css.replace(/font-family:\s*'([^']+)'/g, (_m, name) => `font-family: '${name}${SUFFIX}'`);
        const style = document.createElement('style');
        style.setAttribute('data-sft-font-preview', families.length);
        style.textContent = renamed;
        document.head.appendChild(style);
    } catch {
        // one bad family must not cost the other nineteen their preview:
        // retry them one by one; a single failure just stays in the UI font
        if (families.length > 1) families.forEach((f) => fetchBatch([f]));
    }
};

export const loadFontPreviews = (families) => {
    const todo = families.filter((f) => f && !requested.has(f) && !isSystemStack(f) && !isSymbolFont(f));
    if (todo.length === 0) return;
    todo.forEach((f) => requested.add(f));
    for (let i = 0; i < todo.length; i += BATCH) fetchBatch(todo.slice(i, i + BATCH));
};

/**
 * Callback ref for the Autocomplete listbox (`slotProps={{ listbox: { ref } }}`):
 * observes the options carrying `data-font-family` and loads the visible ones.
 *
 * A plain, stable function rather than a hook: the style tab is built in a
 * plain function where hooks cannot run. Only one font listbox is open at a
 * time, so one module-level cleanup slot is enough; React calls this with the
 * node when the list opens and with null when it closes.
 */
let activeCleanup = null;
export const fontPreviewListboxRef = (node) => {
    if (activeCleanup) { activeCleanup(); activeCleanup = null; }
    if (!node || typeof IntersectionObserver === 'undefined') return;
    let pending = new Set();
    let timer = null;
    const flush = () => { timer = null; loadFontPreviews(Array.from(pending)); pending = new Set(); };
    const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
            if (!e.isIntersecting) return;
            // the selected font is loaded in full already (and must stay so)
            if (e.target.getAttribute('aria-selected') === 'true') return;
            const family = e.target.getAttribute('data-font-family');
            if (family) pending.add(family);
            io.unobserve(e.target);
        });
        // a short pause so a quick scroll becomes one request, not ten
        if (pending.size && !timer) timer = window.setTimeout(flush, 80);
    }, { root: node, rootMargin: '120px 0px' });
    const seen = new WeakSet();
    const watch = () => node.querySelectorAll('[data-font-family]').forEach((el) => {
        if (!seen.has(el)) { seen.add(el); io.observe(el); }
    });
    watch();
    // typing in the field re-renders the options: watch the new ones too
    const mo = new MutationObserver(watch);
    mo.observe(node, { childList: true, subtree: true });
    activeCleanup = () => { io.disconnect(); mo.disconnect(); if (timer) window.clearTimeout(timer); };
};
